// src/composables/useTableExport.js
//
// Shared print / Excel / PDF helpers for tabular reports across the app
// (salary sheets, receipts, statements, calculation lists, ...).
//
// Printing and PDF export both render real HTML (not text-drawn PDF text),
// so Arabic/RTL content always comes out correctly — no font-shaping or
// bidi issues like you'd get trying to draw Arabic text directly with a
// PDF text API.

function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Format a date as D/M/YYYY using plain Western digits (no Arabic-indic
 * numerals, regardless of locale) — used for the "print date" shown on
 * printed/exported reports.
 */
export function formatPrintDate(date = new Date()) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

function cellValue(column, row) {
  const raw = typeof column.format === 'function' ? column.format(row) : row[column.key];
  return raw === null || raw === undefined || raw === '' ? '—' : raw;
}

function buildReportHtml({ title, subtitleLines = [], columns, rows, rtl = true, orientation = 'landscape' }) {
  const dir = rtl ? 'rtl' : 'ltr';
  const thead = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');
  const tbody = rows.length
    ? rows
        .map(
          (r) =>
            `<tr>${columns.map((c) => `<td>${escapeHtml(cellValue(c, r))}</td>`).join('')}</tr>`
        )
        .join('')
    : `<tr><td colspan="${columns.length}" class="empty">${rtl ? 'لا توجد بيانات' : 'No data'}</td></tr>`;

  return `<!DOCTYPE html>
<html dir="${dir}" lang="${rtl ? 'ar' : 'en'}">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 ${orientation}; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Tahoma, Arial, sans-serif; margin: 0; padding: 0; color: #222; }
  .report-header { text-align: center; margin-bottom: 14px; }
  .report-header h1 { font-size: 18px; margin: 0 0 8px; }
  .meta-row { display: flex; justify-content: center; gap: 20px; font-size: 12px; color: #444; flex-wrap: wrap; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: center; }
  th { background: #eef3f8; font-weight: bold; }
  td.empty { padding: 28px; color: #999; }
  tbody tr:nth-child(even) { background: #fafafa; }
</style>
</head>
<body>
  <div class="report-header">
    <h1>${escapeHtml(title)}</h1>
    <div class="meta-row">
      ${subtitleLines.map((l) => `<span>${escapeHtml(l)}</span>`).join('')}
    </div>
  </div>
  <table>
    <thead><tr>${thead}</tr></thead>
    <tbody>${tbody}</tbody>
  </table>
</body>
</html>`;
}

export function useTableExport() {
  /**
   * Open a print-ready window with the given table and trigger the browser
   * print dialog (the user can "Save as PDF" from there too).
   */
  const printTable = (options, existingWindow) => {
    const html = buildReportHtml(options);
    const win = existingWindow || window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onload = () => {
      setTimeout(() => {
        win.focus();
        win.print();
      }, 300);
    };
  };

  /**
   * Open a blank browser tab synchronously (call this immediately inside a
   * click handler, before any `await`) and pass the returned window into
   * `printTable(options, win)` once the data you need has loaded. Browsers
   * only allow `window.open()` when it runs as a direct result of a user
   * gesture — opening it after an `await` gets treated as a script-initiated
   * popup and silently blocked, so any print flow that fetches data first
   * needs this two-step form instead of calling `printTable` alone.
   */
  const openPrintWindow = () => window.open('', '_blank');

  /**
   * Download an .xls file Excel can open directly (HTML-table trick — no
   * extra library needed, and it round-trips Arabic text correctly).
   */
  const exportExcel = ({ title, columns, rows, filename, rtl = true }) => {
    const thead = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');
    const tbody = rows
      .map(
        (r) =>
          `<tr>${columns.map((c) => `<td>${escapeHtml(cellValue(c, r))}</td>`).join('')}</tr>`
      )
      .join('');
    const html = `<html><head><meta charset="UTF-8"></head><body dir="${rtl ? 'rtl' : 'ltr'}">
      <table border="1">
        <thead><tr>${thead}</tr></thead>
        <tbody>${tbody}</tbody>
      </table>
    </body></html>`;
    const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename || title || 'export'}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  /**
   * Render the same report HTML off-screen, snapshot it, and produce a real
   * downloadable PDF (paginated if the table is tall). Uses html2canvas +
   * jsPDF, both already bundled in the app.
   */
  const exportPdf = async (options) => {
    const { title, filename, orientation = 'landscape' } = options;
    const html = buildReportHtml(options);

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-99999px';
    container.style.top = '0';
    document.body.appendChild(container);

    const iframe = document.createElement('iframe');
    iframe.style.width = '1400px';
    iframe.style.height = '10px';
    iframe.style.border = 'none';
    container.appendChild(iframe);

    try {
      await new Promise((resolve) => {
        iframe.onload = resolve;
        iframe.srcdoc = html;
      });

      const doc = iframe.contentDocument;
      const bodyHeight = doc.body.scrollHeight;
      iframe.style.height = `${bodyHeight}px`;

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(doc.body, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${filename || title || 'export'}.pdf`);
    } finally {
      document.body.removeChild(container);
    }
  };

  return { printTable, openPrintWindow, exportExcel, exportPdf };
}
